import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, CreditCard, Mail, PauseCircle, ShieldCheck, TrendingUp, UserPlus } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { KpiCard, Panel } from "@/components/common/Primitives";
import { AccountTable } from "@/components/admin/AccountTable";
import { AccountFormDialog } from "@/components/admin/AccountFormDialog";
import { AccountDetailDialog } from "@/components/admin/AccountDetailDialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { dateTime, eur, num } from "@/lib/format";

export default function Admin() {
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [newInvite, setNewInvite] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testTemplate, setTestTemplate] = useState("invite");
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    try {
      const [overview, log] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/audit", { params: { limit: 60 } }),
      ]);
      setData(overview.data);
      setAudit(log.data.entries || []);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (account, status) => {
    setBusyId(account.id);
    try {
      await api.post(`/admin/accounts/${account.id}/status`, { status });
      toast.success(`${account.name}: ${status === "ACTIVE" ? "geactiveerd" : "gepauzeerd"}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusyId(null);
  };

  const impersonate = async (account) => {
    setBusyId(account.id);
    try {
      await startImpersonation(account.id);
      toast.success(`Je bekijkt ImDash nu als ${account.name}`);
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusyId(null);
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/test-email", {
        to: testEmail,
        template: testTemplate,
      });
      if (data.sent) toast.success(`Voorbeeldmail verstuurd naar ${data.to}`);
      else toast.error("E-mail niet verstuurd: e-mailintegratie is niet geconfigureerd");
    } catch (e) {
      toast.error(apiError(e));
    }
    setSendingTest(false);
  };

  const kpis = data?.kpis || {};

  return (
    <AppShell
      title="ImDash Master Admin"
      subtitle="Klantaccounts, abonnementen en toegang"
      actions={
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="btn-primary"
          data-testid="admin-new-account-button"
        >
          <UserPlus className="mr-1.5 inline h-3.5 w-3.5" /> Nieuwe klant
        </button>
      }
    >
      {error && (
        <div className="panel mb-3 border-red-300 bg-red-50 p-3 text-[13px] text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          label="Totale accounts"
          value={num(kpis.total_accounts)}
          icon={Building2}
          testid="admin-kpi-total"
        />
        <KpiCard
          label="Actief"
          value={num(kpis.active_accounts)}
          tone="positive"
          icon={ShieldCheck}
          testid="admin-kpi-active"
        />
        <KpiCard
          label="Gepauzeerd"
          value={num(kpis.paused_accounts)}
          tone={kpis.paused_accounts ? "warning" : "default"}
          icon={PauseCircle}
          testid="admin-kpi-paused"
        />
        <KpiCard label="Trials" value={num(kpis.trials)} testid="admin-kpi-trials" />
        <KpiCard
          label="Betalingsproblemen"
          value={num(kpis.payment_issues)}
          tone={kpis.payment_issues ? "danger" : "default"}
          icon={CreditCard}
          testid="admin-kpi-payment"
        />
        <KpiCard
          label="Nieuw deze maand"
          value={num(kpis.new_this_month)}
          sub={`Maandomzet ${eur(kpis.monthly_revenue)} · ${num(kpis.test_accounts)} testaccounts`}
          icon={TrendingUp}
          testid="admin-kpi-new"
        />
      </div>

      {newInvite && (
        <div className="panel mt-3 border-blue-200 bg-blue-50 p-3" data-testid="admin-invite-banner">
          <div className="label-xs">Uitnodigingslink voor {newInvite.account?.name}</div>
          <div className="mono mt-1 break-all text-[11px] text-slate-700">
            {newInvite.invite_link}
          </div>
          <div className="mt-1.5 flex gap-2">
            <button
              type="button"
              className="btn-ghost"
              data-testid="admin-copy-invite"
              onClick={() => {
                navigator.clipboard?.writeText(newInvite.invite_link);
                toast.success("Invite link gekopieerd");
              }}
            >
              Invite link kopiëren
            </button>
            <button type="button" className="btn-ghost" onClick={() => setNewInvite(null)}>
              Sluiten
            </button>
          </div>
        </div>
      )}

      <Panel
        className="mt-3"
        title="Klanten"
        subtitle="Alle ImDash-accounts met status, abonnement en laatste login"
        testid="admin-accounts-panel"
      >
        {data ? (
          <AccountTable
            accounts={data.accounts || []}
            onOpen={(account) => setOpenId(account.id)}
            onStatus={setStatus}
            onImpersonate={impersonate}
            busyId={busyId}
          />
        ) : (
          <p className="p-3 text-[13px] text-slate-500">Klanten laden…</p>
        )}
      </Panel>

      <Panel
        className="mt-3"
        title="E-mail testen"
        subtitle="Stuur een voorbeeld van de klantmail naar je eigen adres"
        testid="admin-test-email-panel"
      >
        <div className="flex flex-wrap items-end gap-2 p-3">
          <div>
            <label className="label-xs block">E-mailadres</label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="naam@bedrijf.nl"
              data-testid="admin-test-email-input"
              className="mt-1 h-9 w-64 rounded-sm border border-slate-300 bg-white px-2.5 text-[13px] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
            />
          </div>
          <div>
            <label className="label-xs block">Sjabloon</label>
            <select
              value={testTemplate}
              onChange={(e) => setTestTemplate(e.target.value)}
              data-testid="admin-test-email-template"
              className="mt-1 h-9 rounded-sm border border-slate-300 bg-white px-2.5 text-[13px]"
            >
              <option value="invite">Welkomstmail met uitnodiging</option>
              <option value="password_reset">Wachtwoord opnieuw instellen</option>
            </select>
          </div>
          <button
            type="button"
            onClick={sendTestEmail}
            disabled={sendingTest || !testEmail}
            className="btn-primary"
            data-testid="admin-test-email-send"
          >
            <Mail className="mr-1.5 inline h-3.5 w-3.5" /> Voorbeeld versturen
          </button>
          <p className="w-full text-[11px] text-slate-500">
            De knop in de voorbeeldmail bevat een demolink en activeert geen account.
          </p>
        </div>
      </Panel>

      <Panel className="mt-3" title="Auditlog" subtitle="Alle beheeracties" testid="admin-audit-panel">
        <div className="max-h-72 overflow-y-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tijdstip</th>
                <th>Actie</th>
                <th>Beheerder</th>
                <th>Account</th>
                <th>Wijziging</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((entry) => (
                <tr key={entry.id}>
                  <td className="mono text-[11px]">{dateTime(entry.timestamp)}</td>
                  <td>{entry.action}</td>
                  <td className="text-[12px]">{entry.actor_email || "—"}</td>
                  <td className="mono text-[11px]">{entry.account_id || "—"}</td>
                  <td className="text-[11px] text-slate-500">
                    {Object.keys(entry.changes || {}).length
                      ? Object.entries(entry.changes)
                          .map(([k, v]) => `${k}: ${typeof v === "object" ? "…" : v}`)
                          .join(" · ")
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!audit.length && <p className="p-3 text-[13px] text-slate-500">Nog geen acties.</p>}
        </div>
      </Panel>

      {creating && (
        <AccountFormDialog
          plans={data?.plans || []}
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setNewInvite(result);
            load();
          }}
        />
      )}
      {openId && (
        <AccountDetailDialog
          accountId={openId}
          plans={data?.plans || []}
          onClose={() => setOpenId(null)}
          onChanged={load}
          onImpersonate={impersonate}
        />
      )}
    </AppShell>
  );
}
